import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download, FileText, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function GuideDetail({ slug }) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/cms/guides/slug/${slug}`);
        setGuide(res.data);
      } catch { setGuide(null); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center"><p className="text-sm text-[#999]">Loading...</p></div>;
  if (!guide) return (
    <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center flex-col gap-4">
      <p className="font-serif text-xl">Guide not found</p>
      <Link to="/guides" className="text-sm text-[#6B6B6B] underline">Back to Guides</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="guide-detail-page">
      {guide.cover_image_url && (
        <div className="w-full h-[40vh] overflow-hidden">
          <img src={`${API}${guide.cover_image_url}`} alt={guide.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Link to="/guides" className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#999] hover:text-[#1A1A1A]">
          <ArrowLeft size={12} /> Travel Guides
        </Link>
        <h1 className="font-serif text-3xl text-[#1A1A1A] mt-6 mb-3">{guide.title}</h1>
        <p className="text-base text-[#6B6B6B] mb-8">{guide.description}</p>
        {guide.file_url && (
          <a
            href={`${API}${guide.file_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors"
            data-testid="guide-download-btn"
          >
            <Download size={14} /> Download Guide
          </a>
        )}
      </div>
    </div>
  );
}

export default function GuidesPage() {
  const { slug } = useParams();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) {
      axios.get(`${API}/api/cms/guides/published`)
        .then(res => { setGuides(res.data); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [slug]);

  if (slug) return <GuideDetail slug={slug} />;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="guides-page">
      <section className="pt-20 pb-12 sm:pt-32 sm:pb-20 px-6 text-center">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">Resources</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mb-6">Travel Guides</h1>
        <p className="font-accent italic text-base text-[#6B6B6B] max-w-lg mx-auto leading-relaxed">
          Curated guides to help you prepare for your journey.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-16 sm:pb-32">
        {loading ? (
          <p className="text-sm text-[#999] text-center">Loading...</p>
        ) : guides.length === 0 ? (
          <div className="text-center py-20">
            <FileText size={32} className="mx-auto mb-4 text-[#E8E4DC]" strokeWidth={1} />
            <p className="text-sm text-[#999]">Guides coming soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guides-grid">
            {guides.map(g => (
              <Link
                key={g.id}
                to={`/guides/${g.slug}`}
                className="group block bg-white border border-[#E8E4DC] rounded-lg overflow-hidden hover:border-[#1A1A1A] transition-colors"
                data-testid={`guide-card-${g.slug}`}
              >
                {g.cover_image_url ? (
                  <img src={`${API}${g.cover_image_url}`} alt={g.title} className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-[#F5F3EE] flex items-center justify-center">
                    <FileText size={32} className="text-[#E8E4DC]" strokeWidth={1} />
                  </div>
                )}
                <div className="p-5">
                  <h3 className="font-serif text-base text-[#1A1A1A] group-hover:text-[#333] mb-1">{g.title}</h3>
                  <p className="text-sm text-[#6B6B6B] line-clamp-2">{g.description}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-6 text-center bg-[#F5F3EE] border-t border-[#E8E4DC]" data-testid="guides-cta">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">Curated For You</p>
        <p className="font-accent text-base sm:text-lg text-[#1A1A1A] italic mb-10">
          Looking for something specific? Let us know.
        </p>
        <Link to="/plan" className="px-8 py-3.5 bg-[#1A1A1A] text-white text-sm tracking-wide rounded hover:bg-[#333] transition-colors" data-testid="guides-cta-button">
          Begin Your Experience
        </Link>
      </section>
    </div>
  );
}
